'use client'

import { getFilesUrls } from '@/jobs/storage/getFileUrl'
import { uploadWithTus } from '@/jobs/storage/tus'
import {
  FileInput,
  FileUploader,
  FileUploaderContent,
  FileUploaderItem,
} from '@/presentation/components/fileUpload'
import { UploadIcon } from '@radix-ui/react-icons'
import mime from 'mime'
import Image from 'next/image'
import { useCallback, useEffect, useRef, useState } from 'react'
import { v4 as uuidv4 } from 'uuid'
import type { AutoFormWidgetsProps } from '../autoForm'
import { FieldDescription } from '../description'
import { Field } from '../field'
import { FieldControl } from '../fieldControl'
import { FieldLabel } from '../label'
import { FieldMessage } from '../message'

interface FileWithProgress {
  file: File
  id: string
  progress: number
}

export function SingleFileWidget(props: AutoFormWidgetsProps) {
  const [value, setValue] = useState<FileWithProgress[] | null>(null)
  const [uploadedFile, setUploadedFile] = useState<{ url: string; path: string } | null>(null)
  const [uploadError, setUploadError] = useState<string | null>(null)
  const controller = useRef<AbortController | null>(null)

  useEffect(() => {
    if (typeof props.initialValue === 'string') {
      getFilesUrls([props.initialValue]).then((files) => {
        if (files.length) {
          setUploadedFile({
            url: files[0].url,
            path: props.initialValue,
          })
        }
      })
    }
  }, [props.initialValue])

  const getFileName = useCallback(
    (file: FileWithProgress | { url: string; path: string }): string => {
      if ('file' in file) {
        return (file as FileWithProgress).file.name
      }

      const pathname = new URL(file.url).pathname.split('/').pop()
      return pathname || 'Carregando...'
    },
    [],
  )

  const getFileUrl = useCallback(
    (file: FileWithProgress | { url: string; path: string }): string => {
      if ('url' in file) {
        const url = file.url as string
        const extension = mime.getExtension(mime.getType(url) || '')

        if (extension && mime.getType(url)?.startsWith('image/')) {
          return url
        }

        return '/file.svg'
      }

      // Para arquivos em upload, podemos criar uma URL temporária se for uma imagem
      return file.file.type?.startsWith('image/') ? URL.createObjectURL(file.file) : '/file.svg'
    },
    [],
  )

  const handleFileChange = useCallback(
    async (files: File[] | null) => {
      if (!files) return

      controller.current = new AbortController()
      setUploadError(null)

      setValue([
        {
          file: files[0],
          id: uuidv4(),
          progress: 0,
        },
      ])

      try {
        const fullUrl = await uploadWithTus(
          files[0],
          (props.options?.directory as string) || 'contents',
          (progress) => {
            // Atualiza o progresso do upload
            setValue((prev) => {
              if (!prev) return null
              return prev.map((file) => ({
                ...file,
                progress,
              }))
            })
          },
          controller.current.signal,
        )

        // Extrair a key dentro do bucket e gerar URL assinada (para bucket privado)
        const url = new URL(fullUrl)
        const idx = url.pathname.indexOf('/app/')
        const path = idx >= 0 ? url.pathname.slice(idx + '/app/'.length) : ''
        const signed = path ? await getFilesUrls([path]) : []
        const previewUrl = signed[0]?.url || fullUrl

        setUploadedFile({
          url: previewUrl,
          path,
        })
      } catch (error) {
        if (error instanceof Error && error.message === 'Upload cancelado') {
          console.log('Upload cancelado pelo usuário')
          setUploadError(null)
        } else {
          const errorMessage = error instanceof Error ? error.message : String(error)
          console.error('Erro no upload:', error)
          // Armazenar erro para exibir via FieldMessage
          if (
            errorMessage.includes('Variáveis de ambiente') ||
            errorMessage.includes('Region is missing') ||
            errorMessage.includes('Supabase Storage')
          ) {
            setUploadError(
              'Erro de configuração: As variáveis de ambiente do Supabase Storage não estão configuradas. Entre em contato com o suporte.',
            )
          } else {
            setUploadError(`Erro no upload: ${errorMessage}`)
          }
        }
      } finally {
        controller.current = null
        setValue(null)
      }
    },
    [props.options?.directory],
  )

  const handleDeleteFile = useCallback(() => {
    // Para TUS/Supabase Storage, a deleção pode ser feita via API se necessário
    // Por enquanto, apenas limpa o estado local
    if (uploadedFile) {
      setUploadedFile(null)
      setValue(null)
    }
  }, [uploadedFile])

  return (
    <Field path={props.path} className={props.classNames?.root}>
      <input
        type="text"
        className="hidden"
        name={props.path}
        readOnly
        value={uploadedFile?.path || ''}
      />
      <FieldLabel>{props.options?.label as string}</FieldLabel>
      <FieldDescription>{props.options?.description as string}</FieldDescription>
      <FieldControl className="border-2 border-dashed shadow-sm border-input">
        <FileUploader
          value={value?.map((file) => file.file) || null}
          onValueChange={handleFileChange}
          className="relative bg-white/50 rounded-sm py-2"
          dropzoneOptions={{
            multiple: true,
            maxFiles: 100,
            maxSize: 1024 * 1024 * 4,
          }}
        >
          <FileInput name={props.path}>
            <div className="flex flex-col text-center items-center justify-center w-full py-4">
              <UploadIcon className="h-6 w-6" />
              <p className="mt-2 text-sm text-zinc-600">
                <span className="font-semibold">Arrastar arquivos</span>
              </p>
              <p className="text-xs text-zinc-400">Clique para fazer o upload</p>
            </div>
          </FileInput>

          <FileUploaderContent className="flex items-center flex-row flex-wrap gap-2">
            {value?.map((file) => (
              <div key={file.id}>
                <p>{file.file.name}</p>
                <p>{file.progress}</p>
              </div>
            ))}

            {uploadedFile && (
              <FileUploaderItem
                index={0}
                className="flex items-center flex-row flex-wrap gap-2 p-2 w-full"
                onRemove={handleDeleteFile}
              >
                {(() => {
                  const src = getFileUrl(uploadedFile)
                  return (
                <Image
                  src={src}
                  alt={getFileName(uploadedFile)}
                  height={40}
                  width={40}
                  className="p-0 h-[40px] w-[40px]"
                  unoptimized={src.startsWith('http')}
                />
                  )
                })()}
                <a
                  target="_blank"
                  rel="noreferrer"
                  href={uploadedFile.url}
                  className="text-sm text-zinc-600 font-semibold flex-1"
                >
                  {getFileName(uploadedFile)}
                </a>
              </FileUploaderItem>
            )}
          </FileUploaderContent>
        </FileUploader>
      </FieldControl>
      {uploadError && (
        <p className="text-[0.8rem] font-medium text-destructive mt-1">{uploadError}</p>
      )}
      <FieldMessage />
    </Field>
  )
}
